// Getypte, veilige localStorage-toegang voor de GameOn-sleutels (AGENTS.md §10).
// Nooit crashen: geen storage (privémodus), corrupte JSON of quota-fouten vallen terug op standaardwaarden.
// Let op: een deel van de legacy-waarden is een PLATTE string (bijv. 'gameon_theme' = dark),
// geen JSON. Gebruik daarvoor getString/setString.
import { KEYS } from './keys';

export type StorageKey = (typeof KEYS)[keyof typeof KEYS];

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function getRaw(key: StorageKey): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** true bij succes, false bij quota-fout of geen opslag. */
export function setRaw(key: StorageKey, value: string): boolean {
  try {
    const s = storage();
    if (!s) return false;
    s.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeKey(key: StorageKey): void {
  try {
    storage()?.removeItem(key);
  } catch {
    // negeren
  }
}

/**
 * Leest een platte string. Tolereert ook een JSON-gequote waarde ('"dark"'),
 * voor het geval een tussenversie JSON.stringify gebruikte.
 */
export function getString(key: StorageKey): string | null {
  const raw = getRaw(key);
  if (raw === null) return null;
  if (raw.length >= 2 && raw.startsWith('"') && raw.endsWith('"')) {
    try {
      const v: unknown = JSON.parse(raw);
      if (typeof v === 'string') return v;
    } catch {
      // val terug op de ruwe waarde
    }
  }
  return raw;
}

export function setString(key: StorageKey, value: string): boolean {
  return setRaw(key, value);
}

/** JSON lezen; bij ontbreken/corrupt → fallback. Optionele `validate` mag de geparste waarde omzetten of null teruggeven. */
export function getJSON<T>(key: StorageKey, fallback: T, validate?: (v: unknown) => T | null): T {
  const raw = getRaw(key);
  if (raw === null) return fallback;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!validate) return parsed as T;
    const v = validate(parsed);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
}

export function setJSON(key: StorageKey, value: unknown): boolean {
  try {
    return setRaw(key, JSON.stringify(value));
  } catch {
    return false;
  }
}

export function getFlag(key: StorageKey): boolean {
  return !!getRaw(key);
}
